import { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { StatCard } from '@/components/StatCard'
import { ErrorState, LoadingSkeleton } from '@/components/States'
import { StatusBadge } from '@/components/StatusBadge'
import { UserAvatar } from '@/components/UserAvatar'
import { ActivityTimeline } from '@/components/ActivityTimeline'
import { DocumentDetailDrawer } from '@/components/DocumentDetailDrawer'
import { DataTable, type Column } from '@/components/DataTable'
import { useUser, useActivity, useDocuments, useSignatures } from '@/hooks/useRootQueries'
import { updateUserStatus, downloadDocumentFile } from '@/api/root'
import { toast } from '@/components/Toast'
import { relativeTime, exactTimestamp } from '@/utils/time'
import { formatHash } from '@/utils/format'
import { IconCopy } from '@/components/Icons'
import type { AuditEvent, DocumentItem, SignerItem } from '@/types/api'

type TabType = 'activity' | 'documents' | 'signatures'

export function UserDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [updating, setUpdating] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('activity')
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null)
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)
  const [activitySearch, setActivitySearch] = useState('')
  const [activityAction, setActivityAction] = useState('')
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null)
  const [selectedOrg, setSelectedOrg] = useState<string>('')

  const user = useUser(id)

  const u = user.data
  const userEmail = u?.email || ''
  const isSignerType = u?.userType === 'SIGNER' || (id ? id.startsWith('signer-') : false)
  const isInvitedType = u?.userType === 'INVITED'
  const isDisabled = u ? u.status.toLowerCase() === 'disabled' : false

  // Fetch only this specific user's audit logs
  const activity = useActivity({
    user: userEmail || id || '',
    search: activitySearch || undefined,
    action: activityAction || undefined,
  })

  // Fetch documents owned by this user (platform-wide or filtered by selected organization)
  const userDocuments = useDocuments({
    owner: (!isSignerType && u?.id) ? u.id : undefined,
    organization: selectedOrg || undefined,
    search: isSignerType ? userEmail : undefined,
    pageSize: 100,
  })

  // Fetch signature requests / responses for this user's documents
  const userSignatures = useSignatures({
    owner: (!isSignerType && u?.id) ? u.id : undefined,
    organization: selectedOrg || undefined,
    search: isSignerType ? userEmail : undefined,
    pageSize: 100,
  })

  if (user.isLoading) return <LoadingSkeleton rows={5} />
  if (user.isError || !u) {
    return <ErrorState message="Could not load this user profile." onRetry={() => user.refetch()} />
  }

  async function handleToggleStatus() {
    if (!id || !u) return
    const nextStatus = isDisabled ? 'active' : 'disabled'
    const actionLabel = isDisabled ? 'enable' : 'disable'
    if (!window.confirm(`Are you sure you want to ${actionLabel} account for ${u.name}?`)) return

    try {
      setUpdating(true)
      await updateUserStatus(id, nextStatus)
      toast.success(`User account status updated to ${nextStatus}.`)
      user.refetch()
    } catch {
      toast.error('Failed to update user status.')
    } finally {
      setUpdating(false)
    }
  }

  async function handleDownload(docId: string, title?: string) {
    try {
      setDownloadingDocId(docId)
      toast.info('Initiating document download...')
      await downloadDocumentFile(docId, title ? `${title}.pdf` : undefined)
      toast.success('Document downloaded successfully.')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Download failed'
      toast.error(message)
    } finally {
      setDownloadingDocId(null)
    }
  }

  const documentColumns: Column<DocumentItem>[] = [
    {
      key: 'title',
      header: 'Title',
      render: (d) => (
        <div>
          <button
            onClick={() => setSelectedDocId(d.id)}
            className="font-semibold text-sky-600 hover:text-sky-800 hover:underline text-left block text-xs"
          >
            {d.title}
          </button>
          <span className="font-mono text-[10px] text-slate-400">ID: {d.id}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (d) => <StatusBadge status={d.status} />,
    },
    {
      key: 'category',
      header: 'Category',
      render: (d) => <span className="text-xs text-slate-600 font-medium">{d.category || 'General'}</span>,
    },
    {
      key: 'signers',
      header: 'Assigned Signers & Status',
      render: (d) => {
        const signersList = Array.isArray(d.signers) ? d.signers : []
        if (signersList.length === 0) {
          return <span className="text-slate-400 font-mono text-[10px] italic">No signers</span>
        }
        const signedCount = signersList.filter((s) => s.status?.toLowerCase() === 'signed').length
        return (
          <div className="space-y-1 max-w-xs">
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500 font-bold">
              <span>{signedCount}/{signersList.length} Completed</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {signersList.map((s, idx) => {
                const sStatus = (s.status || '').toLowerCase()
                const isSigned = sStatus === 'signed'
                const isDeclined = sStatus === 'declined' || sStatus === 'voided'
                return (
                  <span
                    key={s.id || idx}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-medium border ${
                      isSigned
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                        : isDeclined
                        ? 'bg-rose-50 text-rose-800 border-rose-200'
                        : 'bg-amber-50 text-amber-800 border-amber-200'
                    }`}
                    title={`${s.name || s.email} (${s.email}) — Status: ${s.status}`}
                  >
                    <span>{isSigned ? '✓' : isDeclined ? '✕' : '⏳'}</span>
                    <span className="font-semibold">{s.name || s.email}</span>
                  </span>
                )
              })}
            </div>
          </div>
        )
      },
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (d) => (
        <span className="font-mono text-xs text-slate-500" title={exactTimestamp(d.createdAt)}>
          {relativeTime(d.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (d) => (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setSelectedDocId(d.id)}
            className="rounded border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors shadow-2xs"
          >
            Inspect
          </button>
          <button
            onClick={() => handleDownload(d.id, d.title)}
            disabled={downloadingDocId === d.id}
            className="rounded border border-sky-300 bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-100 transition-colors shadow-2xs disabled:opacity-50"
            title="Download PDF"
          >
            {downloadingDocId === d.id ? 'Downloading…' : '↓ Download'}
          </button>
        </div>
      ),
    },
  ]

  const signatureColumns: Column<SignerItem>[] = [
    {
      key: 'docTitle',
      header: 'Document Title',
      render: (s) => (
        <div>
          <button
            onClick={() => setSelectedDocId(s.docId)}
            className="font-semibold text-sky-600 hover:text-sky-800 hover:underline text-left block text-xs"
          >
            {s.docTitle || s.docId}
          </button>
          <span className="font-mono text-[10px] text-slate-400">Doc ID: {s.docId}</span>
        </div>
      ),
    },
    {
      key: 'signer',
      header: 'Signer Name / Email',
      render: (s) => (
        <div>
          <div className="font-semibold text-slate-800 text-xs">{s.name || 'Signer'}</div>
          <div className="font-mono text-[10.5px] text-slate-500">{s.email}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Signer Status',
      render: (s) => <StatusBadge status={s.status} />,
    },
    {
      key: 'signatureType',
      header: 'Signature Type',
      render: (s) => (
        <span className="inline-flex items-center gap-1 text-xs text-slate-700 font-medium">
          {s.signatureType?.includes('DSC') ? '🔏' : s.signatureType?.includes('Aadhaar') ? '🆔' : '✍️'}
          <span>{s.signatureType || 'Electronic Signature'}</span>
        </span>
      ),
    },
    {
      key: 'hash',
      header: 'Signature Hash',
      render: (s) =>
        s.signatureHash ? (
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <span
              className="font-mono text-[11px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded inline-block shadow-2xs select-all cursor-pointer hover:border-sky-400"
              title={`Click to copy SHA-256 hash:\n${s.signatureHash}`}
              onClick={() => {
                navigator.clipboard.writeText(s.signatureHash || '')
                toast.success('Signature hash copied to clipboard!')
              }}
            >
              {formatHash(s.signatureHash, 6, 6)}
            </span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(s.signatureHash || '')
                toast.success('Signature hash copied to clipboard!')
              }}
              className="rounded p-1 text-slate-400 hover:text-sky-700 hover:bg-sky-50 transition-colors"
              title="Copy hash"
            >
              <IconCopy className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <span className="text-slate-400 text-[11px]">—</span>
        ),
    },
    {
      key: 'signedAt',
      header: 'Signed At',
      render: (s) => (
        <span className="font-mono text-xs text-slate-500">
          {s.signedAt ? relativeTime(s.signedAt) : 'Pending response'}
        </span>
      ),
    },
    {
      key: 'ipAddress',
      header: 'IP Address',
      render: (s) => <span className="font-mono text-xs text-slate-500">{s.ipAddress || '—'}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (s) => (
        <button
          onClick={() => handleDownload(s.docId, s.docTitle)}
          disabled={downloadingDocId === s.docId}
          className="rounded border border-sky-300 bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-100 transition-colors shadow-2xs disabled:opacity-50"
        >
          {downloadingDocId === s.docId ? 'Downloading…' : '↓ Download PDF'}
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      {/* Navigation Breadcrumb */}
      <button
        onClick={() => navigate('/users')}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-sky-700 transition-colors"
      >
        <span>←</span>
        <span>Back to Platform Users</span>
      </button>

      {/* User Header Profile Card */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <UserAvatar name={u.name || u.email} size={52} />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">{u.name || u.email}</h1>
              <StatusBadge status={u.status} />
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${
                  isSignerType
                    ? 'bg-amber-50 text-amber-800 border-amber-300'
                    : isInvitedType
                    ? 'bg-purple-50 text-purple-800 border-purple-300'
                    : u.role?.toLowerCase().includes('admin')
                    ? 'bg-indigo-50 text-indigo-800 border-indigo-300'
                    : u.role?.toLowerCase().includes('manager')
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                    : 'bg-sky-50 text-sky-800 border-sky-300'
                }`}
              >
                {isSignerType
                  ? '✍️ Sign Request Responder'
                  : isInvitedType
                  ? '✉️ Pending Invitation'
                  : u.role
                  ? `🛡️ ${u.role.toUpperCase()}`
                  : '👤 MEMBER'}
              </span>
            </div>
            <div className="flex items-center gap-2.5 flex-wrap text-xs text-slate-500 mt-1">
              <span className="font-mono font-medium text-slate-700">{u.email}</span>
              <span>·</span>
              <span>
                Organization:{' '}
                {u.orgId && u.orgId !== 'undefined' ? (
                  <Link
                    to={`/organizations/${u.orgId}`}
                    className="font-semibold text-sky-600 hover:text-sky-800 hover:underline"
                  >
                    {u.orgName && u.orgName !== 'Organization'
                      ? u.orgName
                      : (u.organizations && u.organizations[0]?.orgName && u.organizations[0]?.orgName !== 'Organization')
                      ? u.organizations[0].orgName
                      : 'Default Organization'}
                  </Link>
                ) : (
                  <span className="font-semibold text-slate-700">
                    {u.orgName && u.orgName !== 'Organization'
                      ? u.orgName
                      : (u.organizations && u.organizations[0]?.orgName) || 'Default Organization'}
                  </span>
                )}
              </span>
              <span>·</span>
              <span className="font-mono text-slate-400">ID: {u.id}</span>
            </div>

            {u.organizations && u.organizations.length > 0 && (
              <div className="mt-3 pt-2.5 border-t border-slate-100 flex flex-wrap items-center gap-1.5">
                <span className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider mr-1">
                  Tenant Memberships ({u.organizations.length}):
                </span>
                {u.organizations.map((org) => {
                  const isSelected = selectedOrg === org.orgId
                  const displayName = org.orgName && org.orgName !== 'Organization' ? org.orgName : 'Organization'
                  return (
                    <button
                      key={org.orgId}
                      onClick={() => {
                        setSelectedOrg(isSelected ? '' : org.orgId)
                        setActiveTab('documents')
                      }}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-semibold transition-all border shadow-2xs ${
                        isSelected
                          ? 'bg-sky-600 text-white border-sky-600 shadow-sm'
                          : 'bg-slate-50 hover:bg-sky-50 text-slate-700 hover:text-sky-700 border-slate-200 hover:border-sky-300'
                      }`}
                      title={`Click to view documents created in ${displayName}`}
                    >
                      <span>🏢 {displayName}</span>
                      {org.role && (
                        <span className="text-[10px] opacity-75 font-normal capitalize">
                          ({org.role})
                        </span>
                      )}
                      <span
                        className={`rounded-full px-1.5 py-0.2 text-[9.5px] font-mono font-bold ${
                          isSelected ? 'bg-sky-700 text-white' : 'bg-white border border-slate-200 text-slate-700'
                        }`}
                      >
                        {org.documentCount} {org.documentCount === 1 ? 'doc' : 'docs'}
                      </span>
                    </button>
                  )
                })}
                {selectedOrg && (
                  <button
                    onClick={() => setSelectedOrg('')}
                    className="text-[11px] font-semibold text-rose-600 hover:text-rose-800 hover:underline ml-1"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!isSignerType && !isInvitedType && (
            <button
              onClick={handleToggleStatus}
              disabled={updating}
              className={`rounded-lg px-4 py-2 font-mono text-xs font-bold transition-all shadow-xs ${
                isDisabled
                  ? 'border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                  : 'border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100'
              }`}
            >
              {updating ? 'Updating…' : isDisabled ? '✓ Enable Account' : '⚠️ Disable Account'}
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="User Role"
          value={isSignerType ? 'Signer' : isInvitedType ? 'Invited' : (u.role ? (u.role.charAt(0).toUpperCase() + u.role.slice(1).toLowerCase()) : 'Member')}
          icon={u.role?.toLowerCase().includes('admin') ? '👑' : isSignerType ? '✍️' : '👤'}
        />
        <StatCard
          label="Total Documents"
          value={u.documentsCount ?? (Array.isArray(userDocuments.data?.data) ? userDocuments.data.data.length : 0)}
          icon="📄"
        />
        <StatCard
          label="Completed / Signed"
          value={u.completedCount ?? (Array.isArray(userSignatures.data?.data) ? userSignatures.data.data.filter(s => s.status?.toLowerCase() === 'signed').length : 0)}
          icon="✅"
          tone="up"
        />
        <StatCard
          label="Last Active"
          value={u.lastActivityAt ? relativeTime(u.lastActivityAt) : u.lastLoginAt ? relativeTime(u.lastLoginAt) : 'Never'}
          icon="🕒"
        />
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-slate-200">
        <nav className="-mb-px flex space-x-6">
          <button
            onClick={() => setActiveTab('activity')}
            className={`py-3 px-1 border-b-2 font-semibold text-xs transition-colors flex items-center gap-2 ${
              activeTab === 'activity'
                ? 'border-sky-600 text-sky-700 font-bold'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <span>🛡️ User Audit Trail</span>
            {activity.data?.meta && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono text-slate-600">
                {activity.data.meta.totalItems}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('documents')}
            className={`py-3 px-1 border-b-2 font-semibold text-xs transition-colors flex items-center gap-2 ${
              activeTab === 'documents'
                ? 'border-sky-600 text-sky-700 font-bold'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <span>📄 Associated Documents</span>
            {userDocuments.data?.meta && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono text-slate-600">
                {userDocuments.data.meta.totalItems}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('signatures')}
            className={`py-3 px-1 border-b-2 font-semibold text-xs transition-colors flex items-center gap-2 ${
              activeTab === 'signatures'
                ? 'border-sky-600 text-sky-700 font-bold'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <span>✍️ Signature Transactions</span>
            {userSignatures.data?.meta && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono text-slate-600">
                {userSignatures.data.meta.totalItems}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'activity' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
            <div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                User Audit Log (Strictly Isolated for {u.email})
              </h2>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Displays only authentication events, document signing actions, and interactions performed by this specific user.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="text"
                value={activitySearch}
                onChange={(e) => setActivitySearch(e.target.value)}
                placeholder="Filter user events…"
                className="h-8 rounded-lg border border-slate-200 px-2.5 text-xs focus:border-sky-500 focus:outline-none"
              />
              <select
                value={activityAction}
                onChange={(e) => setActivityAction(e.target.value)}
                className="h-8 rounded-lg border border-slate-200 px-2 text-xs font-medium text-slate-700 focus:border-sky-500 focus:outline-none"
              >
                <option value="">All Actions</option>
                <option value="LOGIN">Logins</option>
                <option value="DOCUMENT_SIGNED">Signatures</option>
                <option value="DOCUMENT_CREATED">Document Uploads</option>
                <option value="DOCUMENT_SENT">Sent Requests</option>
                <option value="SECURITY">Security / Failure</option>
              </select>
            </div>
          </div>

          {activity.isLoading && <LoadingSkeleton rows={4} />}
          {activity.isError && <ErrorState message="Could not load user activity log." onRetry={() => activity.refetch()} />}
          {!activity.isLoading && !activity.isError && (!Array.isArray(activity.data?.data) || activity.data.data.length === 0) && (
            <div className="py-12 text-center">
              <div className="text-3xl mb-2">📜</div>
              <h3 className="text-sm font-semibold text-slate-700">No Audit Events Found</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                No individual audit trail entries recorded for {u.email} matching the current filters.
              </p>
            </div>
          )}
          {!activity.isLoading && !activity.isError && Array.isArray(activity.data?.data) && activity.data.data.length > 0 && (
            <ActivityTimeline
              events={activity.data.data}
              onSelect={(e) => {
                setSelectedEvent(e)
                if (e.documentId || e.document_id) {
                  setSelectedDocId(e.documentId || e.document_id || null)
                }
              }}
            />
          )}

          <div className="mt-4 border-t border-slate-200 pt-3 font-mono text-[11px] text-slate-500" title={exactTimestamp(u.createdAt)}>
            Profile registered / created on {exactTimestamp(u.createdAt)}.
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-4">
          {u.organizations && u.organizations.length > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-2xs">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1">Filter Organization:</span>
                <button
                  onClick={() => setSelectedOrg('')}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all border ${
                    !selectedOrg
                      ? 'bg-sky-600 text-white border-sky-600 shadow-2xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  All Organizations ({u.documentsCount ?? 0})
                </button>
                {u.organizations.map((org) => (
                  <button
                    key={org.orgId}
                    onClick={() => setSelectedOrg(org.orgId)}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all border ${
                      selectedOrg === org.orgId
                        ? 'bg-sky-600 text-white border-sky-600 shadow-2xs'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    🏢 {org.orgName} ({org.documentCount})
                  </button>
                ))}
              </div>
              {selectedOrg && (
                <span className="text-[11px] font-medium text-slate-500">
                  Filtered: {userDocuments.data?.data?.length ?? 0} documents
                </span>
              )}
            </div>
          )}

          <DataTable<DocumentItem>
            rows={userDocuments.data?.data || []}
            columns={documentColumns}
            getRowId={(row) => row.id}
            isLoading={userDocuments.isLoading}
            isError={userDocuments.isError}
            emptyTitle="No Documents Found"
            emptyDescription={
              selectedOrg
                ? `No documents found for this user in the selected organization.`
                : `No documents found for user ${u.name || u.email}.`
            }
            onRowClick={(row) => setSelectedDocId(row.id)}
          />
        </div>
      )}

      {activeTab === 'signatures' && (
        <div className="space-y-4">
          {u.organizations && u.organizations.length > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-2xs">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1">Filter Organization:</span>
                <button
                  onClick={() => setSelectedOrg('')}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all border ${
                    !selectedOrg
                      ? 'bg-sky-600 text-white border-sky-600 shadow-2xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  All Organizations
                </button>
                {u.organizations.map((org) => (
                  <button
                    key={org.orgId}
                    onClick={() => setSelectedOrg(org.orgId)}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all border ${
                      selectedOrg === org.orgId
                        ? 'bg-sky-600 text-white border-sky-600 shadow-2xs'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    🏢 {org.orgName} ({org.signedCount ?? 0})
                  </button>
                ))}
              </div>
            </div>
          )}

          <DataTable<SignerItem>
            rows={userSignatures.data?.data || []}
            columns={signatureColumns}
            getRowId={(row) => row.id || `${row.docId}-${row.email}`}
            isLoading={userSignatures.isLoading}
            isError={userSignatures.isError}
            emptyTitle="No Signature Records Found"
            emptyDescription={
              selectedOrg
                ? `No signature records found in the selected organization.`
                : `No signature records found for ${u.email}.`
            }
            onRowClick={(row) => setSelectedDocId(row.docId)}
          />
        </div>
      )}

      {/* Document Detail Drawer */}
      <DocumentDetailDrawer
        open={!!selectedDocId}
        onClose={() => setSelectedDocId(null)}
        documentId={selectedDocId}
        event={selectedEvent}
      />
    </div>
  )
}
